/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MatchModule */

import {
    Component,
    EventEmitter,
    Input,
    Output,
} from '@angular/core';

import { HostHdrMatch } from 'object-types';
import { HdrMatchOperation } from 'generated-types';
import { IAviDropdownOption } from 'standalone-components/avi-dropdown';
import { createOptionsFromEnumProps } from 'ng/shared/utils/dropdown.utils';
import { L10nService } from '@vmw/ngx-vip';
import { SchemaService } from 'ajs/modules/core/services/schema-service';

import { HostHdrMatchConfigItem }
    from 'ajs/modules/match/factories/host-hdr-match.config-item.factory';

import { LocationHdrMatchConfigItem }
    from 'ajs/modules/match/factories/location-hdr-match.config-item.factory';

import * as l10n from './host-hdr-match.l10n';
import './host-hdr-match.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

const preventConfigureValueSet = new Set([
    HdrMatchOperation.HDR_EXISTS,
    HdrMatchOperation.HDR_DOES_NOT_EXIST,
]);

/**
 * @description HostHdrMatch component.
 * Also used as LocationHdrMatch component.
 * @author Akul Aggarwal, Abhinesh Gour
 */
@Component({
    selector: 'host-hdr-match',
    templateUrl: './host-hdr-match.component.html',
})
export class HostHdrMatchComponent {
    /**
     * Method Match message item instances, can be either of
     * HostHdrMatchConfigItem or LocationHdrMatchConfigItem.
     */
    @Input()
    public editable: HostHdrMatchConfigItem | LocationHdrMatchConfigItem;

    /**
     * Index of the match within the MatchAdder component
     */
    @Input()
    public matchIndex = 0;

    /**
     * Match label passed to the MatchWrapperComponent
     */
    @Input()
    public label: string;

    /**
     * fieldName passed to the MatchWrapperComponent
     */
    @Input()
    public fieldName: string;

    /**
     * objectType passed to the MatchWrapperComponent
     */
    @Input()
    public objectType: string;

    /**
     * Disable any modifications to the fields.
     */
    @Input()
    public disabled = false;

    /**
     * Message item ObjectType passed to this match component for fields in the template.
     * Enables reusing same component for different match types.
     * Value can be HostHdrMatch or LocationHdrMatch.
     */
    @Input()
    public matchObjectType = HostHdrMatch;

    /**
     * EventEmitter for removing a match, passed to the MatchWrapperComponent
     */
    @Output()
    public onRemoveMatch = new EventEmitter<void>();

    public readonly l10nKeys = l10nKeys;

    /**
     * Dropdown options for the match operation.
     */
    public criteriaOptions: IAviDropdownOption[];

    constructor(
        l10nService: L10nService,
        private readonly schemaService: SchemaService,
    ) {
        l10nService.registerSourceBundles(dictionary);
        this.setCriteriaOptions();
    }

    /**
     * Returns true if the value input field should be shown.
     */
    public showValueField(): boolean {
        return this.editable.matchCriteria && !this.preventConfigureValue();
    }

    /**
     * Called when the match criteria changes.
     */
    public handleCriteriaChange(): void {
        if (this.preventConfigureValue()) {
            delete this.editable.config.value;
        }
    }

    /**
     * Called to remove this match. Passed to the MatchWrapperComponent.
     */
    public removeMatch(): void {
        this.onRemoveMatch.emit();
    }

    /**
     * Return if the match configuration is invalid.
     */
    public get isMatchConfigInvalid(): boolean {
        return !this.preventConfigureValue() &&
            this.editable.config.value &&
            !this.editable.config.value.length;
    }

    /**
     * True if the value field should not be configurable.
     */
    private preventConfigureValue(): boolean {
        return preventConfigureValueSet.has(this.editable.matchCriteria);
    }

    /**
     * Sets criteriaOptions
     */
    private setCriteriaOptions(): void {
        const criterion = this.schemaService.getEnumValues('HdrMatchOperation');

        this.criteriaOptions = createOptionsFromEnumProps(criterion);
    }
}
